import {
    Button,
    DialogInfo,
    FramePageContainer,
    LingodaClassIcon,
    Portal,
    Select,
    type SelectProps,
    Stack,
} from '@lingoda/ui';
import { ClassType } from '@lingoda/monolith-graphql';
import { useToggleState } from '@lingoda/hooks';
import { trans } from '@lingoda/i18n';
import { BookingOverlay } from '../../BookingOverlay';
import { IndividualClassBookingForm } from '../individual/IndividualClassBookingForm';
import { type WidgetClassType } from '../types';
import type { FC } from 'react';

interface Props {
    classType: WidgetClassType;
    onClassTypeChange: (value: WidgetClassType) => void;
    moduleId: number;
    lessonId: number;
    portalContainer?: Element | null;
}

export const BookingWidgetMobile = ({
    classType,
    onClassTypeChange,
    moduleId,
    lessonId,
    portalContainer,
}: Props) => {
    const [isBookingModalOpen, openBookingModal, closeBookingModal] = useToggleState();

    const handleTypeChange: SelectProps<ClassTypeOption>['onChange'] = (event) => {
        onClassTypeChange(event.target.value as WidgetClassType);
    };

    return (
        <Portal container={portalContainer}>
            <FramePageContainer pt={2} pb={2} data-cy="Booking widget">
                <Stack spacing={2}>
                    <Select
                        value={classType}
                        onChange={handleTypeChange}
                        options={typeOptions}
                        getOptionValue={(option) => option.id}
                        getOptionLabel={(option) => option.label}
                        getOptionIcon={(option) => classTypeIcons[option.id]}
                        MenuProps={SelectMenuProps}
                    />
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={openBookingModal}
                        fullWidth
                    >
                        {trans('btn-book-class', {}, 'student-common')}
                    </Button>
                </Stack>
                <BookingOverlay
                    moduleId={moduleId}
                    lessonId={lessonId}
                    open={classType === ClassType.Group && isBookingModalOpen}
                    onClose={closeBookingModal}
                />
                <DialogInfo
                    title={trans('book-individual-class', {}, 'student-common')}
                    isOpen={classType === ClassType.Individual && isBookingModalOpen}
                    onClose={closeBookingModal}
                    isButtonVisible={false}
                >
                    <IndividualClassBookingForm moduleId={moduleId} lessonId={lessonId} />
                </DialogInfo>
            </FramePageContainer>
        </Portal>
    );
};

type ClassTypeOption = {
    id: WidgetClassType;
    label: string;
};

const IndividualClassIcon = () => <LingodaClassIcon type="individual" size={16} />;
const GroupClassIcon = () => <LingodaClassIcon type="group" size={16} />;

const classTypeIcons: Record<string, FC> = {
    [ClassType.Individual]: IndividualClassIcon,
    [ClassType.Group]: GroupClassIcon,
};

const typeOptions: ClassTypeOption[] = [
    {
        id: ClassType.Group,
        label: trans('group-class', {}, 'student-common'),
    },
    {
        id: ClassType.Individual,
        label: trans('individual-class', {}, 'student-common'),
    },
];

const SelectMenuProps = {
    anchorOrigin: { vertical: 'top', horizontal: 'left' },
    transformOrigin: { vertical: 'bottom', horizontal: 'left' },
} as const;
